import { useState, useEffect, useCallback, useRef } from 'react';
import CategoryManager from './AdminCategory';
import AdminUsers from './AdminUsers';
import AdminActivity from './AdminActivity';
import AdminScrapLog from './AdminScrapLog';
import AdminReviews from './AdminReviews';
import Header from './components/Header';

const STATUS_LABEL = {
  none:    { text: '시작 안됨',  color: '#aaa' },
  pending: { text: '진행 중',    color: '#f59e0b' },
  done:    { text: '완료',       color: '#16a34a' },
};

function ScrapStatusBadge({ status, done, total }) {
  const label = STATUS_LABEL[status];
  if (!label) return null;
  return (
    <>
      <div style={{ fontSize: 24, fontWeight: 'bold', color: label.color, marginBottom: 4 }}>
        {label.text}
      </div>
      {total > 0 && (
        <div style={{ fontSize: 13, color: '#888' }}>{done} / {total} 완료</div>
      )}
    </>
  );
}

function TodayStatus() {
  const [scrapStatus, setScrapStatus] = useState(null);

  const fetchStatus = useCallback(async () => {
    const res = await fetch('/api/admin/scrap/today');
    const data = await res.json();
    setScrapStatus(data);
    if (data.status === 'pending') setTimeout(fetchStatus, 5000);
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 14, color: '#555', marginBottom: 8 }}>오늘 스크랩 상태</div>
      {scrapStatus
        ? <ScrapStatusBadge {...scrapStatus} />
        : <div style={{ fontSize: 13, color: '#aaa' }}>불러오는 중...</div>
      }
    </div>
  );
}

function ReprocessPanel() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [pollStatus, setPollStatus] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = useCallback((d) => {
    stopPolling();
    const tick = async () => {
      const res = await fetch(`/api/admin/scrap/status?date=${d}`);
      const data = await res.json();
      setPollStatus(data);
      if (!data.processing) stopPolling();
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
  }, []);

  useEffect(() => () => stopPolling(), []);

  // 마운트 시 현재 날짜 상태 확인 → 처리 중이면 폴링 재개
  useEffect(() => {
    fetch(`/api/admin/scrap/status?date=${date}`)
      .then(res => res.json())
      .then(data => {
        setPollStatus(data);
        if (data.processing) startPolling(date);
      });
  }, [date, startPolling]);

  const isProcessing = loading || pollStatus?.processing;

  const handleReprocess = async () => {
    if (!window.confirm(`${date} 데이터를 삭제하고 재처리할까요?`)) return;
    setLoading(true);
    setResult(null);
    setPollStatus(null);
    try {
      const res = await fetch('/api/admin/reprocess', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '오류 발생');
      setResult(data);
      startPolling(date);
    } catch (e) {
      setResult({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 14, color: '#555', marginBottom: 12 }}>날짜별 재처리</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          type="date"
          value={date}
          max={today}
          onChange={e => { setDate(e.target.value); setResult(null); setPollStatus(null); stopPolling(); }}
          style={{ flex: 1, padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 }}
        />
        <button
          onClick={handleReprocess}
          disabled={isProcessing}
          style={{
            padding: '6px 16px', borderRadius: 6, border: 'none', cursor: isProcessing ? 'not-allowed' : 'pointer',
            background: isProcessing ? '#ccc' : '#ef4444', color: 'white', fontWeight: 'bold', fontSize: 14,
          }}
        >
          {loading ? '요청 중...' : isProcessing ? '처리 중...' : '재처리'}
        </button>
      </div>

      {result && !result.error && (
        <div style={{ fontSize: 12, color: '#555', background: '#f9f9f9', borderRadius: 6, padding: 10, marginBottom: 8 }}>
          <div style={{ marginBottom: 4 }}>
            <strong>삭제 완료</strong> · BrightData {result.reset}건 초기화
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px' }}>
            {Object.entries(result.deleted).map(([t, c]) => (
              <span key={t} style={{ color: c > 0 ? '#333' : '#bbb' }}>{t}: {c}</span>
            ))}
          </div>
        </div>
      )}

      {result?.error && (
        <div style={{ fontSize: 13, color: '#ef4444', marginBottom: 8 }}>{result.error}</div>
      )}

      {pollStatus && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
            처리 상태 {pollStatus.processing && <span style={{ color: '#f59e0b' }}>· snapshot 처리 중...</span>}
          </div>
          <ScrapStatusBadge {...pollStatus} />
        </div>
      )}
    </div>
  );
}

const cardStyle = {
  background: 'white',
  padding: '24px 32px',
  borderRadius: 12,
  boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
  textAlign: 'center',
  minWidth: 320,
};

function Admin() {
  const [limitPerInput, setLimitPerInput] = useState(null);
  const [tab, setTab] = useState('categories');

  useEffect(() => {
    fetch('/api/admin/config')
      .then(res => res.json())
      .then(data => setLimitPerInput(data.limitPerInput));
  }, []);

  const tabBtnStyle = (active) => ({
    border: 0,
    background: 'none',
    padding: '8px 12px',
    borderBottom: active ? '2px solid #5a5af0' : '2px solid transparent',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    fontSize: 14,
  });

  return (
    <>
      <Header />
      <div style={{ display: 'flex', gap: 12, borderBottom: '1px solid #eee', padding: '12px 16px', background: '#fff' }}>
        <button onClick={() => setTab('categories')} style={tabBtnStyle(tab === 'categories')}>카테고리 관리</button>
        <button onClick={() => setTab('users')} style={tabBtnStyle(tab === 'users')}>사용자 관리</button>
        <button onClick={() => setTab('activity')} style={tabBtnStyle(tab === 'activity')}>로그</button>
        <button onClick={() => setTab('scrap')} style={tabBtnStyle(tab === 'scrap')}>스크랩</button>
        <button onClick={() => setTab('scrap-log')} style={tabBtnStyle(tab === 'scrap-log')}>스크랩 로그</button>
        <button onClick={() => setTab('reviews')} style={tabBtnStyle(tab === 'reviews')}>리뷰 수집</button>
        <button onClick={() => setTab('details')} style={tabBtnStyle(tab === 'details')}>상세 수집</button>
      </div>

      {tab === 'categories' && (
        <div style={{ padding: '16px', background: '#f5f5f5', minHeight: '100vh' }}>
          <CategoryManager />
        </div>
      )}

      {tab === 'users' && (
        <div style={{ padding: '16px', background: '#f5f5f5', minHeight: '100vh' }}>
          <AdminUsers />
        </div>
      )}

      {tab === 'activity' && (
        <div style={{ padding: '16px', background: '#f5f5f5', minHeight: '100vh' }}>
          <AdminActivity />
        </div>
      )}

      {tab === 'scrap' && (
        <div style={{ padding: '16px', background: '#f5f5f5', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
          {limitPerInput !== null && (
            <div style={{ fontSize: 13, color: '#888' }}>
              BRIGHTDATA_LIMIT_PER_INPUT: <strong>{limitPerInput}</strong>
            </div>
          )}
          <TodayStatus />
          <ReprocessPanel />
        </div>
      )}

      {tab === 'scrap-log' && (
        <div style={{ padding: '16px', background: '#f5f5f5', minHeight: '100vh' }}>
          <AdminScrapLog />
        </div>
      )}

      {tab === 'reviews' && (
        <div style={{ padding: '16px', background: '#f5f5f5', minHeight: '100vh' }}>
          <AdminReviews />
        </div>
      )}

      {tab === 'details' && (
        <div style={{ padding: '16px', background: '#f5f5f5', minHeight: '100vh' }}>
          <AdminReviews kind="detail" />
        </div>
      )}
    </>
  );
}

export default Admin;
